import React, { useState, useMemo } from 'react';
import { Row } from 'react-bootstrap';
import { colorPalette } from '../../../../utils/theme';
import MaterialIcon from '../../../../components/material-icon';
import CTAButton from '../../../../components/cta-button/cta-button';
import useIsMobile from '../../../../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { SectionBadge, SectionSubtitle, SectionTitle } from '../../sub-pages/home-page/home-page.styles';
import { AccordionContainer, AccordionHeader, AccordionItem, AccordionPanel, AnswerContent, Badge, Heading, IconButton, LeadText, LeftColumn, NavyBlock, QuestionText, RightColumn, Section, StyledContainer } from './faq-section.styles';
import useContactUsModalStore from '../../../../stores/contactUsModalStore';

interface FAQItem {
  id: string;
  question: string;
  answer?: string;
}

interface FAQSectionProps {
  id?: string;
  style?: React.CSSProperties;
  items?: FAQItem[];
}

const getInvestorFAQItems = (t: TFunction): FAQItem[] => [
  {
    id: 'faq-1',
    question: t('pages.home.faq.items.faq_1.question'),
    answer: t('pages.home.faq.items.faq_1.answer')
  },
  {
    id: 'faq-2',
    question: t('pages.home.faq.items.faq_2.question'),
    answer: t('pages.home.faq.items.faq_2.answer')
  },
  {
    id: 'faq-3',
    question: t('pages.home.faq.items.faq_3.question'),
    answer: t('pages.home.faq.items.faq_3.answer')
  },
  {
    id: 'faq-4',
    question: t('pages.home.faq.items.faq_4.question'),
    answer: t('pages.home.faq.items.faq_4.answer')
  },
  {
    id: 'faq-5',
    question: t('pages.home.faq.items.faq_5.question'),
    answer: t('pages.home.faq.items.faq_5.answer')
  }
];

// Function to get FAQ content (badge, title, etc.) for investors
const getInvestorFAQContent = (t: TFunction) => ({
  badge: t('pages.home.faq.badge'),
  sectionTitle: t('pages.home.faq.section_title'),
  sectionSubtitle: t('pages.home.faq.section_subtitle'),
  faqBadge: t('pages.home.faq.faq_badge'),
  faqHeading: t('pages.home.faq.faq_heading'),
  faqLeadText: t('pages.home.faq.faq_lead_text'),
  contactButton: t('pages.home.faq.contact_button')
});

const FAQSection: React.FC<FAQSectionProps> = ({
  id,
  style,
  items,
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Stores
  const setIsContactUsModalVisible = useContactUsModalStore(state => state.setIsVisible);

  // States
  const [openItem, setOpenItem] = useState<string>('');

  // Generate FAQ items and content from translations if no custom items provided
  const faqItems = useMemo(() => {
    return items || getInvestorFAQItems(t);
  }, [items, t]);

  const faqContent = useMemo(() => {
    return getInvestorFAQContent(t);
  }, [t]);

  const handleToggle = (id: string) => {
    setOpenItem(openItem === id ? '' : id);
  };

  const handleKeyDown = (event: React.KeyboardEvent, id: string, index: number) => {
    const headerButtons = document.querySelectorAll('[data-faq-header]');

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleToggle(id);
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = (index + 1) % faqItems.length;
        (headerButtons[nextIndex] as HTMLElement)?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = (index - 1 + faqItems.length) % faqItems.length;
        (headerButtons[prevIndex] as HTMLElement)?.focus();
        break;
      case 'Home':
        event.preventDefault();
        (headerButtons[0] as HTMLElement)?.focus();
        break;
      case 'End':
        event.preventDefault();
        (headerButtons[faqItems.length - 1] as HTMLElement)?.focus();
        break;
    }
  };

  return (
    <Section id={id} style={style}>
      <StyledContainer className="text-center">
        <SectionBadge>{faqContent.badge}</SectionBadge>
        <SectionTitle>
          {faqContent.sectionTitle}
        </SectionTitle>
        <SectionSubtitle>
          {faqContent.sectionSubtitle}
        </SectionSubtitle>
        <NavyBlock className="text-start">
          <Row className={isMobile ? "g-4" : "g-5"}>
            <LeftColumn lg={5}>
              <Badge>{faqContent.faqBadge}</Badge>
              <Heading>{faqContent.faqHeading}</Heading>
              <LeadText>
                {faqContent.faqLeadText}
              </LeadText>
              <div>
                <CTAButton
                  variant="secondary"
                  size={isMobile ? "XS" : "M"}
                  label={faqContent.contactButton}
                  icon="chat"
                  onClick={() => setIsContactUsModalVisible(true)}
                />
              </div>
            </LeftColumn>

            <RightColumn lg={7}>
              <AccordionContainer>
                {faqItems.map((item, index) => (
                  <AccordionItem key={item.id}>
                    <AccordionHeader
                      data-faq-header
                      aria-expanded={openItem === item.id}
                      aria-controls={`panel-${item.id}`}
                      id={`header-${item.id}`}
                      onClick={() => handleToggle(item.id)}
                      onKeyDown={(e) => handleKeyDown(e, item.id, index)}
                    >
                      <QuestionText>{item.question}</QuestionText>
                      <IconButton isOpen={openItem === item.id}>
                        <MaterialIcon
                          icon="add"
                          size={24}
                          color={colorPalette.white}
                        />
                      </IconButton>
                    </AccordionHeader>

                    <AccordionPanel
                      id={`panel-${item.id}`}
                      role="region"
                      aria-labelledby={`header-${item.id}`}
                      isOpen={openItem === item.id}
                    >
                      {item.answer && (
                        <AnswerContent>
                          {item.answer}
                        </AnswerContent>
                      )}
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </AccordionContainer>
            </RightColumn>
          </Row>
        </NavyBlock>
      </StyledContainer>
    </Section>
  );
};

export { getInvestorFAQItems, getInvestorFAQContent };
export default FAQSection;
